import React, { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import toast from 'react-hot-toast';

import {
  DOCUMENT_DEFINITIONS,
  useFinancialVerification,
} from './FinancialVerificationContext';
import PrimaryButton from '../../../shared/buttons/PrimaryButton';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILE_SIZE_MB = MAX_FILE_SIZE / (1024 * 1024);
const MAX_FILES = 6;

export default function BankStatements({ onBankConnection }) {
  const {
    bankStatementsFiles,
    shouldShowPlaidConnection,
    uploadBankStatements,
    removeBankStatements,
  } = useFinancialVerification();
  const fileInputRef = useRef(null);
  const files = bankStatementsFiles || [];

  const handleUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const remainingSlots = MAX_FILES - files.length;
    const showFileCapacityError = (slots) => {
      toast.error(
        `Cannot upload more than ${MAX_FILES} files. You can upload ${Math.max(
          slots,
          0
        )} more file(s).`
      );
    };

    if (remainingSlots <= 0) {
      showFileCapacityError(remainingSlots);
      return;
    }

    let filesToUpload = selectedFiles;
    if (selectedFiles.length > remainingSlots) {
      showFileCapacityError(remainingSlots);
      filesToUpload = selectedFiles.slice(0, remainingSlots);
    }

    const oversizedFiles = filesToUpload.filter((file) => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      toast.error(
        `Some files are too large. Please upload files smaller than ${MAX_FILE_SIZE_MB}MB.`
      );
      return;
    }

    filesToUpload.forEach((file) => {
      uploadBankStatements(file);
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-white border border-primary-border-gray rounded-lg p-4 mb-6 flex gap-2">
      <div className="w-2/3 flex flex-col gap-2">
        <h3 className="text-base">Upload Bank Statements</h3>
        <p className="text-xs pr-6">
          {DOCUMENT_DEFINITIONS.bank_statements.title}
        </p>
        <div className="text-xs max-w-max rounded-md p-1.5 text-secondary-gray bg-secondary-blue font-medium mt-2">
          Slower Approval
        </div>

        {files.length > 0 && (
          <div className="mt-2 flex w-full gap-2 flex-wrap">
            {files.map((file) => (
              <div
                key={file.name}
                className="flex w-36 gap-2 justify-between items-center text-xs border rounded-full py-1 px-4"
              >
                <span className="truncate">{file.name}</span>
                <FontAwesomeIcon
                  icon="close"
                  className="cursor-pointer hover:text-secondary-gray flex-shrink-0"
                  onClick={() => removeBankStatements(file)}
                />
              </div>
            ))}
          </div>
        )}

        {shouldShowPlaidConnection && (
          <div className="mt-1">
            <button
              onClick={onBankConnection}
              className="text-primary-blue hover:text-primary-blue-dark text-xs"
            >
              Link bank account with Plaid instead
            </button>
          </div>
        )}
      </div>

      <div className="w-1/3 flex items-center justify-end">
        <PrimaryButton
          onClick={handleUpload}
          buttonText="Upload"
          ariaLabel="Upload"
          buttonColor="bg-primary-blue"
          hoverClass="hover:bg-primary-blue-hover"
          disabled={files.length >= MAX_FILES}
        />
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
        onChange={handleFileSelect}
        multiple
        style={{ display: 'none' }}
      />
    </div>
  );
}
